/**
 * The small pieces every page uses: skeletons, empty states, a confirm modal,
 * a copy button, relative times and the status dot.
 *
 * They live together because each is a dozen lines and the point of them is
 * consistency — one shape of "still loading", one shape of "nothing here yet,
 * do this", one shape of "are you sure". A destructive action anywhere in this
 * app goes through `ConfirmModal`.
 */

import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import { Link } from "react-router";
import { errorMessage } from "../api";

/* ── Brand ────────────────────────────────────────────────────────────────── */

export function BrandMark({ size = 26 }: { size?: number }): ReactNode {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" className="emb-brand-mark" aria-hidden="true">
      <rect width="32" height="32" rx="8" fill="var(--emb-accent)" />
      <g fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.5 11 6 16l4.5 5" />
        <path d="M21.5 11 26 16l-4.5 5" />
        <path d="M18.2 9.2 13.8 22.8" />
      </g>
    </svg>
  );
}

/* ── Loading ──────────────────────────────────────────────────────────────── */

export function Skeleton({ width = "100%", height = "1rem", className = "" }: { width?: string; height?: string; className?: string }): ReactNode {
  return <span className={`emb-skeleton d-block ${className}`} style={{ width, height }} aria-hidden="true" />;
}

/** A few grey lines in the shape of the thing that is coming. */
export function SkeletonRows({ rows = 3, className = "" }: { rows?: number; className?: string }): ReactNode {
  return (
    <div className={`d-flex flex-column gap-2 ${className}`} role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} width={index === rows - 1 ? "62%" : "100%"} height="1.05rem" />
      ))}
    </div>
  );
}

export function InlineSpinner({ label }: { label: string }): ReactNode {
  return (
    <span className="d-inline-flex align-items-center gap-2 emb-muted small">
      <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
      {label}
    </span>
  );
}

/* ── Errors and emptiness ─────────────────────────────────────────────────── */

export function ErrorAlert({ error, onRetry }: { error: string; onRetry?: () => void }): ReactNode {
  return (
    <Alert variant="danger" className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-0">
      <span>{error}</span>
      {onRetry ? (
        <Button size="sm" variant="outline-danger" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </Alert>
  );
}

/**
 * What an empty list says. Always two parts: what is not here, and the one
 * thing to do about it — an empty state that only says "no data" leaves the
 * reader to guess whether something is broken.
 */
export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }): ReactNode {
  return (
    <div className="text-center py-4 px-3">
      <p className="fw-semibold mb-1">{title}</p>
      {children ? <p className="emb-muted small mb-3 mx-auto" style={{ maxWidth: "34rem" }}>{children}</p> : null}
      {action}
    </div>
  );
}

/* ── Confirm ──────────────────────────────────────────────────────────────── */

export interface ConfirmModalProps {
  show: boolean;
  title: string;
  confirmLabel: string;
  variant?: "danger" | "primary";
  busy?: boolean;
  error?: string | null;
  children: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  show,
  title,
  confirmLabel,
  variant = "danger",
  busy = false,
  error = null,
  children,
  onConfirm,
  onCancel,
}: ConfirmModalProps): ReactNode {
  return (
    <Modal show={show} onHide={busy ? () => undefined : onCancel} centered backdrop={busy ? "static" : true}>
      <Modal.Header closeButton={!busy}>
        <Modal.Title as="h2" className="h5 mb-0">
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {children}
        {error ? (
          <Alert variant="danger" className="mt-3 mb-0">
            {error}
          </Alert>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant={variant} onClick={onConfirm} disabled={busy}>
          {busy ? <Spinner animation="border" size="sm" role="status" aria-hidden="true" /> : confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * Drives a ConfirmModal for a list of similar destructive actions: open it
 * with the subject, run the action, close on success, show the failure in the
 * modal on error. One of these per page beats one piece of state per row.
 */
export function useConfirm<T>(): {
  subject: T | null;
  busy: boolean;
  error: string | null;
  ask: (subject: T) => void;
  cancel: () => void;
  run: (action: (subject: T) => Promise<void>) => Promise<void>;
} {
  const [subject, setSubject] = useState<T | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ask = useCallback((next: T) => {
    setSubject(next);
    setError(null);
  }, []);

  const cancel = useCallback(() => {
    setSubject(null);
    setError(null);
  }, []);

  const run = useCallback(
    async (action: (subject: T) => Promise<void>) => {
      if (subject === null) return;
      setBusy(true);
      setError(null);
      try {
        await action(subject);
        setSubject(null);
      } catch (cause) {
        setError(errorMessage(cause));
      } finally {
        setBusy(false);
      }
    },
    [subject],
  );

  return { subject, busy, error, ask, cancel, run };
}

/* ── Copy ─────────────────────────────────────────────────────────────────── */

export function CopyButton({
  value,
  label = "Copy",
  size = "sm",
  variant = "outline-secondary",
  className = "",
}: {
  value: string;
  label?: string;
  size?: "sm" | "lg";
  variant?: string;
  className?: string;
}): ReactNode {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setState("copied");
    } catch {
      // Clipboard access can be refused (an insecure origin, a permissions
      // policy). Say so rather than pretending it worked.
      setState("failed");
    }
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setState("idle"), 2000);
  }, [value]);

  return (
    <Button variant={variant} size={size} className={className} onClick={() => void copy()}>
      {state === "copied" ? "Copied" : state === "failed" ? "Press Ctrl+C" : label}
    </Button>
  );
}

/** A URL or id shown as code, with a copy button beside it. */
export function CopyField({ value, label }: { value: string; label?: string }): ReactNode {
  const id = useId();
  return (
    <div>
      {label ? (
        <label className="form-label small emb-muted mb-1" htmlFor={id}>
          {label}
        </label>
      ) : null}
      <div className="d-flex align-items-center gap-2 emb-sunken px-3 py-2">
        <code id={id} className="emb-url flex-grow-1">
          {value}
        </code>
        <CopyButton value={value} />
      </div>
    </div>
  );
}

/* ── Time and status ──────────────────────────────────────────────────────── */

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * 24 * 3600_000],
  ["month", 30 * 24 * 3600_000],
  ["day", 24 * 3600_000],
  ["hour", 3600_000],
  ["minute", 60_000],
  ["second", 1000],
];

const relative = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

export function formatRelative(iso: string | null): string {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "unknown";
  const delta = then - Date.now();
  for (const [unit, ms] of UNITS) {
    if (Math.abs(delta) >= ms || unit === "second") return relative.format(Math.round(delta / ms), unit);
  }
  return "just now";
}

export function formatAbsolute(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isFinite(date.getTime()) ? date.toLocaleString() : "—";
}

/** Relative for reading, absolute in the tooltip for the times that matter. */
export function TimeAgo({ iso }: { iso: string | null }): ReactNode {
  if (!iso) return <span className="emb-muted">never</span>;
  return <time dateTime={iso} title={formatAbsolute(iso)}>{formatRelative(iso)}</time>;
}

export function StatusDot({ online, label }: { online: boolean; label?: string }): ReactNode {
  return (
    <span className="d-inline-flex align-items-center gap-2">
      <span className={`emb-dot${online ? " emb-dot-online" : ""}`} aria-hidden="true" />
      <span>{label ?? (online ? "Online" : "Offline")}</span>
    </span>
  );
}

/* ── JSON ─────────────────────────────────────────────────────────────────── */

export function JsonBlock({ value }: { value: unknown }): ReactNode {
  return <pre className="emb-code emb-mono">{JSON.stringify(value, null, 2)}</pre>;
}

/** A read-only JSON viewer in a modal, with copy. Used for bundles and module source. */
export function ViewerModal({
  show,
  title,
  subtitle,
  body,
  loading,
  error,
  onHide,
}: {
  show: boolean;
  title: string;
  subtitle?: string;
  body: string | null;
  loading?: boolean;
  error?: string | null;
  onHide: () => void;
}): ReactNode {
  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <div>
          <Modal.Title as="h2" className="h5 mb-0">
            {title}
          </Modal.Title>
          {subtitle ? <div className="emb-muted small">{subtitle}</div> : null}
        </div>
      </Modal.Header>
      <Modal.Body>
        {loading ? <SkeletonRows rows={8} /> : error ? <ErrorAlert error={error} /> : <pre className="emb-code emb-mono">{body}</pre>}
      </Modal.Body>
      <Modal.Footer>
        {body && !loading && !error ? <CopyButton value={body} label="Copy all" /> : null}
        <Button variant="light" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ── Links that look like buttons ─────────────────────────────────────────── */

/**
 * A router link styled as a Bootstrap button.
 *
 * react-bootstrap's `<Button as={Link}>` loses the `to` prop's type — the
 * polymorphic `as` widens it away — and a navigation rendered as a <button>
 * cannot be opened in a new tab. This renders the anchor the router wants and
 * borrows the button's classes.
 */
export function LinkButton({
  to,
  variant = "primary",
  size,
  className = "",
  children,
}: {
  to: string;
  variant?: string;
  size?: "sm" | "lg";
  className?: string;
  children: ReactNode;
}): ReactNode {
  const classes = ["btn", `btn-${variant}`, size ? `btn-${size}` : "", className].filter(Boolean).join(" ");
  return (
    <Link to={to} className={classes}>
      {children}
    </Link>
  );
}
